import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { DetailedRadio } from "@amzn/stencil-react-components/radio";
import { Text } from "@amzn/stencil-react-components/text";
import React, { useState } from "react";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import type {
  AddressFields,
  AddressSuggestion,
} from "@/services/address-validation/address-validation.types";
import { formatAddressParagraph } from "./address-suggestions-modal.util";

const STRING_PATH = "addressSuggestionsModal";
const TWO_COLUMN_MODAL_MAX_WIDTH = 720;

export type AddressSuggestionsModalMode = "validWithChanges" | "invalidWithSuggestions";

type SelectedAddress = "suggested" | "entered";

export interface AddressSuggestionsModalProps {
  close: () => void;
  mode: AddressSuggestionsModalMode;
  enteredAddress: AddressFields;
  suggestion: AddressSuggestion;
  handleValidAddress: (address: AddressFields) => void;
}

interface AddressOptionRadioProps {
  titleText: string;
  address: AddressFields;
  testId: string;
  isSelected: boolean;
  onSelect: () => void;
}

const AddressOptionRadio: React.FC<AddressOptionRadioProps> = ({
  titleText,
  address,
  testId,
  isSelected,
  onSelect,
}) => (
  <DetailedRadio
    data-testid={testId}
    name="address-suggestion"
    titleText={titleText}
    details={formatAddressParagraph(address)}
    checked={isSelected}
    onChange={onSelect}
  />
);

export const AddressSuggestionsModal: React.FC<AddressSuggestionsModalProps> = ({
  close,
  mode,
  enteredAddress,
  suggestion,
  handleValidAddress,
}) => {
  const isValidWithChanges = mode === "validWithChanges";
  const [selectedAddress, setSelectedAddress] = useState<SelectedAddress>("suggested");

  const suggestedOption = (
    <AddressOptionRadio
      titleText={getString(`${STRING_PATH}.suggestedAddress`)}
      address={suggestion.address}
      testId="address-suggestions-modal-suggestion"
      isSelected={selectedAddress === "suggested"}
      onSelect={() => setSelectedAddress("suggested")}
    />
  );

  const handleContinue = () =>
    handleValidAddress(selectedAddress === "entered" ? enteredAddress : suggestion.address);

  const buttons = [
    ...(isValidWithChanges
      ? []
      : [
          <Button
            key="edit-my-address"
            data-testid="address-suggestions-modal-edit-my-address"
            onClick={close}
          >
            {getString(`${STRING_PATH}.editMyAddress`)}
          </Button>,
        ]),
    <Button
      key="continue"
      data-testid="address-suggestions-modal-continue"
      variant={ButtonVariant.Primary}
      onClick={handleContinue}
    >
      {getString(`${STRING_PATH}.continue`)}
    </Button>,
  ];

  return (
    <ModalContent
      data-testid="address-suggestions-modal"
      titleText={getString(`${STRING_PATH}.${mode}Title`)}
      onCloseButtonClick={close}
      buttons={buttons}
      maxWidth={isValidWithChanges ? TWO_COLUMN_MODAL_MAX_WIDTH : undefined}
    >
      <Col gridGap="S400" width="100%">
        <Text data-testid="address-suggestions-modal-description" color="neutral70">
          {getString(`${STRING_PATH}.${mode}Description`)}
        </Text>
        {isValidWithChanges ? (
          <Row gridGap="S400" width="100%">
            <Col width="50%">{suggestedOption}</Col>
            <Col width="50%">
              <AddressOptionRadio
                titleText={getString(`${STRING_PATH}.yourAddress`)}
                address={enteredAddress}
                testId="address-suggestions-modal-entered-option"
                isSelected={selectedAddress === "entered"}
                onSelect={() => setSelectedAddress("entered")}
              />
            </Col>
          </Row>
        ) : (
          suggestedOption
        )}
      </Col>
    </ModalContent>
  );
};
